'use client';

import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import Input from '../ui/inputFormik';
import StarIcon from '@/components/ui/icons/star-icon';
import { object, number, string } from 'yup';
import Modal from '../ui/modals';
import { reviewProduct } from '@/services/user/orders';
import { toast } from '@/utils/toast';
import { getCookie } from 'cookies-next';

const ReviewModal = ({ open, onClose, t, entity_id }) => {
  const [hovered, setHovered] = useState(0);
  const mage = getCookie('mage');

  const ReviewSchema = object().shape({
    nickname: string().required(t('Name is required')),
    detail: string().required(t('Please enter your review')),
    rating: number().required(t('Please select a rating')).min(1),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);
      const response = await reviewProduct({
        detail: values?.detail,
        nickname: values.nickname,
        rate: values.rating,
        mage,
        entity_id,
      });

      if (response?.error === null) {
        toast.success(t('Review submitted successfully'));
        resetForm();
        onClose();
      } else {
        toast.error(t('failed_to_add_review'));
      }
    } catch (error) {
      toast.error(error?.message || t('failed_to_add_review'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      title={t('Write a Review')}
      onClose={onClose}
      maxWidth='max-w-lg'
    >
      <div className='p-4'>
        <Formik
          initialValues={{
            nickname: '',
            detail: '',
            rating: 0,
          }}
          validationSchema={ReviewSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className='space-y-4'>
              <Input
                name='nickname'
                id='nickname'
                title={t('Your Name')}
                placeholder={t('Enter your name')}
              />

              {/* Rating Stars */}
              <div>
                <label className='mb-1 block text-sm font-medium'>
                  {t('Your Rating')}
                </label>
                <div className='flex gap-2'>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type='button'
                      onClick={() => setFieldValue('rating', star)}
                      onMouseEnter={() => setHovered(star)}
                      onMouseLeave={() => setHovered(0)}
                    >
                      <StarIcon
                        size={24}
                        className={`${
                          star <= (hovered || values.rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Review Text */}
              <div className='flex flex-col'>
                <label
                  htmlFor='detail'
                  className='mb-1 font-medium max-lg:text-sm'
                >
                  {t('Your Review')}
                </label>
                <Field
                  as='textarea'
                  name='detail'
                  id='detail'
                  rows='4'
                  placeholder={t('Share your experience')}
                  className='resize-none rounded-lg border border-[#E4E4F9] px-2 py-1.5 outline-none'
                />
              </div>

              {/* Buttons */}
              <div className='flex items-center gap-3 pt-3'>
                <button
                  type='submit'
                  disabled={
                    isSubmitting ||
                    !values?.nickname ||
                    !values?.detail ||
                    !values?.rating
                  }
                  className='disabled:bg-primary/60 bg-primary hover:bg-primary/90 flex items-center gap-1 rounded-lg px-5 py-2 text-sm font-medium text-white'
                >
                  {isSubmitting && (
                    <span className='size-4 animate-spin rounded-full border-t border-r border-white max-lg:size-2' />
                  )}
                  {isSubmitting ? t('Submitting') : t('Submit Review')}
                </button>

                <button
                  type='button'
                  onClick={onClose}
                  className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50'
                >
                  {t('Cancel')}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default ReviewModal;
